/**
 * ReservationsReport — the "Where you eat" card.
 *
 * Mirrors TravelReport's grammar: summary strip (bookings · restaurants ·
 * upcoming · most booked), an UPCOMING section with an accent rail and
 * relative countdowns, then YOUR REGULARS ranked by visits with a muted
 * "+N other places, once each" footer. Data merges the calendar-derived
 * reservations with booking-platform email confirmations.
 */

export interface ReservationRow {
  name: string;
  city?: string;
  whenISO: string;
  date: string;
}

interface Venue {
  name: string;
  city?: string;
  count: number;
  lastISO: string;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
  return hasTime
    ? `${day}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : day;
}

function inDays(iso: string, now: Date): string {
  const days = Math.ceil((new Date(iso).getTime() - now.getTime()) / 86_400_000);
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

function lastAgo(iso: string, now: Date): string {
  const d = new Date(iso);
  const days = Math.floor((now.getTime() - d.getTime()) / 86_400_000);
  if (days < 14) return `${Math.max(days, 1)} days ago`;
  if (days < 60) return `last ${Math.round(days / 7)} wks ago`;
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  return d.getFullYear() === now.getFullYear() ? `last ${month}` : `last ${month} ${d.getFullYear()}`;
}

export function ReservationsReport({ reservations }: { reservations: ReservationRow[] }) {
  const now = new Date();
  const upcoming = reservations
    .filter((r) => new Date(r.whenISO).getTime() >= now.getTime())
    .sort((a, b) => a.whenISO.localeCompare(b.whenISO));

  const venues = new Map<string, Venue>();
  for (const r of reservations) {
    const k = r.name.toLowerCase();
    const v = venues.get(k);
    if (v) {
      v.count += 1;
      if (r.whenISO > v.lastISO) v.lastISO = r.whenISO;
      if (r.city && !v.city) v.city = r.city;
    } else {
      venues.set(k, { name: r.name, city: r.city, count: 1, lastISO: r.whenISO });
    }
  }
  const ranked = [...venues.values()].sort((a, b) => b.count - a.count || b.lastISO.localeCompare(a.lastISO));
  const regulars = ranked.filter((v) => v.count >= 2);
  const singles = ranked.filter((v) => v.count === 1);
  const mostRecent = [...reservations].sort((a, b) => b.whenISO.localeCompare(a.whenISO))[0];

  return (
    <div className="otr orr">
      <div className="otr-label">Reservations</div>
      <div className="otr-heading">Where you eat</div>
      <div className="otr-stats">
        <div className="otr-stat">
          <div className="otr-stat-n">{reservations.length}</div>
          <div className="otr-stat-label">bookings · 12 mo</div>
        </div>
        <div className="otr-stat">
          <div className="otr-stat-n">{venues.size}</div>
          <div className="otr-stat-label">restaurants</div>
        </div>
        <div className="otr-stat">
          <div className="otr-stat-n orr-accent">{upcoming.length}</div>
          <div className="otr-stat-label orr-accent">upcoming</div>
        </div>
        {mostRecent && (
          <div className="otr-stat">
            <div className="otr-stat-n orr-stat-name">{mostRecent.name}</div>
            <div className="otr-stat-label">most recent</div>
          </div>
        )}
      </div>

      {upcoming.length > 0 && (
        <>
          <div className="orr-section-label orr-accent">Upcoming</div>
          <div className="orr-upcoming">
            {upcoming.map((r, i) => (
              <div className="orr-up-row" key={i}>
                <div className="orr-up-main">
                  <div className="orr-name">{r.name}</div>
                  {r.city && <div className="orr-sub">{r.city}</div>}
                </div>
                <div className="orr-up-when">
                  <div className="orr-when">{fmtWhen(r.whenISO)}</div>
                  <div className="orr-rel orr-accent">{inDays(r.whenISO, now)}</div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {regulars.length > 0 && (
        <>
          <div className="orr-section-label">Your regulars</div>
          {regulars.slice(0, 6).map((v) => (
            <div className="orr-reg-row" key={v.name}>
              <div className="orr-reg-name">
                {v.name}
                <span className="orr-visits orr-accent"> {v.count} visits</span>
              </div>
              <div className="orr-reg-city">{v.city ?? ''}</div>
              <div className="orr-reg-last">{lastAgo(v.lastISO, now)}</div>
            </div>
          ))}
        </>
      )}
      {singles.length > 0 && (
        <>
          <div className="orr-section-label">{regulars.length > 0 ? 'Once each' : 'Where you went'}</div>
          {singles
            .sort((a, b) => b.lastISO.localeCompare(a.lastISO))
            .slice(0, 12)
            .map((v) => (
              <div className="orr-reg-row" key={v.name}>
                <div className="orr-reg-name">{v.name}</div>
                <div className="orr-reg-city">{v.city ?? ''}</div>
                <div className="orr-reg-last">{lastAgo(v.lastISO, now)}</div>
              </div>
            ))}
          {singles.length > 12 && (
            <div className="orr-singles">+ {singles.length - 12} more, once each</div>
          )}
        </>
      )}
    </div>
  );
}
